import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { MasterProduct, UpdateMasterProductPayload } from './types';
import { masterProductsEndpoint } from './use-get-master-products-api';

interface Params {
  id: number;
  data: UpdateMasterProductPayload;
}

type TData = MasterProduct;
type TError = AxiosError;
type TVariables = Params;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// PUT api/v1/cms/master-products/:id
export function useUpdateMasterProductApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, data }: TVariables): Promise<TData> => {
    // The API wraps the resource in an envelope: { data: {...} } — see
    // use-get-master-product-api.ts for the same fix on the GET-by-id side.
    const response = await http.put(`${masterProductsEndpoint}/${id}`, data);
    return response.data.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${masterProductsEndpoint}/update`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([masterProductsEndpoint]);
      queryClient.invalidateQueries([masterProductsEndpoint, 'detail', variables.id]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
